import {
  ReactNode,
  createContext,
  useContext,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react'

export interface PageHeaderSlots {
  /** Replaces the default route-glyph + title crumb. Must contain the page's h1. */
  crumb?: ReactNode
  /** Page controls rendered right-aligned in the bar, before the Help button. */
  actions?: ReactNode
}

interface PageHeaderSlotsContextValue {
  slots: PageHeaderSlots | null
  setSlots: (slots: PageHeaderSlots | null) => void
}

const PageHeaderSlotsContext = createContext<PageHeaderSlotsContextValue>({
  slots: null,
  setSlots: () => undefined,
})

/**
 * Lets a page project content into the shell's PageHeaderBar — a custom
 * identity crumb and/or trailing action buttons — without the bar knowing
 * about any specific page (the Aria chat is the first consumer). The bar
 * falls back to its default route-glyph + title crumb when no page has
 * registered, so it keeps working with no provider mounted (tests).
 */
export function PageHeaderSlotsProvider({ children }: { children: ReactNode }) {
  const [slots, setSlots] = useState<PageHeaderSlots | null>(null)
  const value = useMemo(() => ({ slots, setSlots }), [slots])
  return (
    <PageHeaderSlotsContext.Provider value={value}>{children}</PageHeaderSlotsContext.Provider>
  )
}

export function usePageHeaderSlots(): PageHeaderSlots | null {
  return useContext(PageHeaderSlotsContext).slots
}

/**
 * Register page-specific header content; cleared automatically on unmount so
 * the next route gets the default crumb back.
 *
 * Callers must keep the `slots` object identity stable across renders that
 * shouldn't re-publish (memoize it). Do **not** put changing list data in the
 * memo deps — read it from a ref inside click handlers instead. An unstable
 * identity re-enters every provider child via `setSlots` and becomes
 * "Maximum update depth exceeded".
 */
export function usePageHeaderSlot(slots: PageHeaderSlots) {
  const { setSlots } = useContext(PageHeaderSlotsContext)

  useLayoutEffect(() => {
    setSlots(slots)
  }, [setSlots, slots])

  // Clear only on unmount. Clearing in the update-effect cleanup
  // (`set null` → `set next`) re-renders every consumer between publishes and
  // turns an unstable slots identity into an infinite loop.
  useLayoutEffect(() => {
    return () => setSlots(null)
  }, [setSlots])
}
